import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import { focusablePropTypes, focusableDefaultProps } from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import styled from 'styled-components';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var TYPES = Object.freeze({
  NUMBER: 'number',
  TEXT: 'text',
  TAGS: 'tags',
  SEARCH: 'search',
  PASSWORD: 'password'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var INPUT_MODE = Object.freeze({
  NONE: 'none',
  TEXT: 'text',
  DECIMAL: 'decimal',
  NUMERIC: 'numeric',
  TEL: 'tel',
  SEARCH: 'search',
  EMAIL: 'email',
  URL: 'url'
});

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var COMMON_PROP_TYPES = _objectSpread({
  id: PropTypes.stringWithDescription('Id of the input.'),
  // eslint-disable-next-line max-len
  autoFocus: PropTypes.boolWithDescription('Flag to focus the input on page load. The autoFocus prop should not be used, as [it can reduce usability and accessibility for users](https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/566011b89df811b95bc4e99799a5ea03d440411d/docs/rules/no-autofocus.md). '),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  defaultValue: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.number, PropTypes.array], 'Default value of the input.'),
  autoCapitalize: PropTypes.stringWithDescription('Flag to auto capitalize the text introduced. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autocapitalize) for full list of values. **Only for mobile**'),
  autoCorrect: PropTypes.oneOfWithDescription(['on', 'off'], 'Flag to activate/deactivate the auto correction in the input. **Only for Safari**'),
  spellCheck: PropTypes.oneOfWithDescription(['true', 'false'], 'Flag to define if the input may be checked for spelling errors.'),
  type: PropTypes.stringWithDescription('Type of the input. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types) for the full list of types.'),
  form: PropTypes.stringWithDescription('Id of the form that the input belongs to.'),
  value: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.number, PropTypes.array], 'Value of the input (controlled).'),
  prefix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the beginning of the input. In case of function, `({ disabled, invalid, value }) => ReactNode`.'),
  suffix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the end of the input. In case of function, `({ disabled, invalid, value }) => ReactNode`'),
  name: PropTypes.stringWithDescription('Name of the input.'),
  readOnly: PropTypes.boolWithDescription('Flag to make the input readonly.'),
  invalid: PropTypes.boolWithDescription('Flag to make the input invalid.'),
  onFocus: PropTypes.funcWithDescription('Callback called when focus event is triggered. `(event) => {}`'),
  onBlur: PropTypes.funcWithDescription('Callback called when blur event is triggered. `(event) => {}`'),
  onChange: PropTypes.funcWithDescription('Callback called when the value of the input has changed. `({ event, value }) => {}`')
}, focusablePropTypes);

var COMMON_DEFAULT_PROPS = _objectSpread({
  id: undefined,
  autoFocus: false,
  className: '',
  autoCapitalize: 'off',
  autoCorrect: undefined,
  spellCheck: undefined,
  defaultValue: undefined,
  type: TYPES.TEXT,
  form: undefined,
  value: undefined,
  prefix: null,
  suffix: null,
  name: null,
  readOnly: false,
  invalid: false,
  onFocus: noop,
  onBlur: noop,
  onChange: noop
}, focusableDefaultProps);

var TEXT_PROP_TYPES = _objectSpread(_objectSpread({}, COMMON_PROP_TYPES), {}, {
  value: PropTypes.stringWithDescription('Value of the input (controlled).'),
  defaultValue: PropTypes.stringWithDescription('Default value of the input.'),
  autoComplete: PropTypes.stringWithDescription('Flag to specify what kind of assistance in filling the field. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values) for full list of values.'),
  inputMode: PropTypes.oneOfWithDescription(Object.values(INPUT_MODE), 'The inputMode hints at the type of data that might be entered by the user, allowing that the browser displays an appropiate virtual keyboard. **Only for mobile**'),
  placeholder: PropTypes.stringWithDescription('Placeholder of the input.'),
  maxLength: PropTypes.numberWithDescription('Maximum length of the input.'),
  minLength: PropTypes.numberWithDescription('Minimum length of the input.')
});

var TEXT_DEFAULT_PROPS = _objectSpread(_objectSpread({}, COMMON_DEFAULT_PROPS), {}, {
  autoComplete: 'on',
  inputMode: INPUT_MODE.TEXT,
  placeholder: null,
  maxLength: 524288,
  minLength: 0
});

var NUMBER_PROP_TYPES = _objectSpread(_objectSpread({}, COMMON_PROP_TYPES), {}, {
  defaultValue: PropTypes.numberWithDescription('Default value of the input (uncontrolled).'),
  value: PropTypes.numberWithDescription('Value of the input (controlled).'),
  autoComplete: PropTypes.stringWithDescription('Flag to specify what kind of assistance in filling the field. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values) for full list of values.'),
  placeholder: PropTypes.stringWithDescription('Placeholder of the input.'),
  precision: PropTypes.numberWithDescription('Number of decimals of to use. By default, it will take the bigger number of decimals between the value and step.'),
  max: PropTypes.numberWithDescription('The maximum value of the input.'),
  min: PropTypes.numberWithDescription('The minimum value of the input.'),
  step: PropTypes.numberWithDescription('Interval between numbers of the input. Allows decimal values.'),
  noStepper: PropTypes.boolWithDescription('Flag to hide the stepper buttons.'),
  prefix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the beginning of the input. In case of function, `({ disabled, invalid, value, min, max, step, precision, increase, decrease }) => ReactNode` (`increase` and `decrease` are functions).'),
  suffix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the end of the input. In case of function, `({ disabled, invalid, value, min, max, step, precision, increase, decrease }) => ReactNode` (`increase` and `decrease` are functions).')
});

var NUMBER_DEFAULT_PROPS = _objectSpread(_objectSpread({}, COMMON_DEFAULT_PROPS), {}, {
  spellCheck: 'false',
  autoComplete: 'on',
  placeholder: null,
  precision: null,
  max: null,
  min: null,
  step: 1,
  noStepper: false
});

var TAGS_PROP_TYPES = _objectSpread(_objectSpread({}, TEXT_PROP_TYPES), {}, {
  defaultValue: PropTypes.arrayOfWithDescription(PropTypes.node, 'Default value of the input (uncontrolled).'),
  value: PropTypes.arrayOfWithDescription(PropTypes.node, 'Value of the input (controlled).'),
  avoidCreateTagOnBlur: PropTypes.boolWithDescription('Flag to deactivate the creation of a tag on input blur.'),
  onTagCreated: PropTypes.funcWithDescription('Callback called when a new tag has been created. `({ tagCreated }) => {}`'),
  onTagRemoved: PropTypes.funcWithDescription('Callback called when a tag has been removed. `({ tagRemoved }) => {}`'),
  onInput: PropTypes.funcWithDescription('Callback called when a user writes in the input.'),
  onChange: PropTypes.funcWithDescription('Callback called when the value of the input has changed. `({ value, tagCreated, tagRemoved }) => {}`')
});

var TAGS_DEFAULT_PROPS = _objectSpread(_objectSpread({}, TEXT_DEFAULT_PROPS), {}, {
  avoidCreateTagOnBlur: false,
  onTagCreated: noop,
  onTagRemoved: noop
});

var SEARCH_PROP_TYPES = _objectSpread(_objectSpread({}, TEXT_PROP_TYPES), {}, {
  prefix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the beginning of the input. In case of function, `({ disabled, invalid, value, clearInput: Function, focusInput: Function }) => ReactNode`.'),
  suffix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the end of the input. In case of function, `({ disabled, invalid, value, clearInput: Function, focusInput: Function }) => ReactNode`'),
  onClear: PropTypes.funcWithDescription('Callback called when the clear button is clicked.')
});

var SEARCH_DEFAULT_PROPS = _objectSpread(_objectSpread({}, TEXT_DEFAULT_PROPS), {}, {
  onClear: noop
});

var PASSWORD_PROP_TYPES = _objectSpread(_objectSpread({}, TEXT_PROP_TYPES), {}, {
  visibility: PropTypes.boolWithDescription('Flag to control the visibility of the input programmatically.'),
  prefix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the beginning of the input. In case of function, `({ disabled, invalid, value, changeVisibility: Function }) => ReactNode`.'),
  suffix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the end of the input. In case of function, `({ disabled, invalid, value, changeVisibility: Function }) => ReactNode`'),
  onChangeVisibility: PropTypes.funcWithDescription('Callback called when the password visibility has changed. `({ visibility }) => {}`')
});

var PASSWORD_DEFAULT_PROPS = _objectSpread(_objectSpread({}, TEXT_DEFAULT_PROPS), {}, {
  autoComplete: undefined,
  onChangeVisibility: noop,
  visibility: false
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Prefix = styled.div.attrs({
  className: 'gz-input__prefix'
}).withConfig({
  componentId: "gz-input-1675939611112__sc-i8ngeb-0"
})(["align-items:center;display:flex;flex:none;justify-content:center;.gz-icon{display:flex;height:12px;width:12px;}"]);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Container = styled.div.withConfig({
  componentId: "gz-input-1675939611112__sc-1j3kuw1-0"
})(["background-color:#fff;border:1px solid #000;border-radius:4px;box-sizing:border-box;display:flex;line-height:20px;margin:0px;min-width:0;overflow:hidden;position:relative;width:100%;.gz-input{background-color:#fff;border:none;display:inline-flex;line-height:20px;margin:0px;min-width:0;outline:0;padding:4px 12px;position:relative;width:100%;&:placeholder-shown{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}}", " ~ .gz-input{padding-left:0;}&:focus-within{outline:2px solid #314fff;outline-offset:2px;}"], Prefix);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Suffix = styled.div.attrs({
  className: 'gz-input__suffix'
}).withConfig({
  componentId: "gz-input-1675939611112__sc-uez3qw-0"
})(["align-items:center;display:flex;flex:none;justify-content:center;.gz-icon{display:flex;height:12px;width:12px;}"]);

export { COMMON_PROP_TYPES as C, INPUT_MODE as I, NUMBER_DEFAULT_PROPS as N, Prefix as P, Suffix as S, TYPES as T, COMMON_DEFAULT_PROPS as a, TEXT_PROP_TYPES as b, TEXT_DEFAULT_PROPS as c, NUMBER_PROP_TYPES as d, Container as e, TAGS_PROP_TYPES as f, TAGS_DEFAULT_PROPS as g, SEARCH_PROP_TYPES as h, SEARCH_DEFAULT_PROPS as i, PASSWORD_PROP_TYPES as j, PASSWORD_DEFAULT_PROPS as k };
